import { useState } from 'react';
import { Badge } from '@/ui/Badge';
import { Button } from '@/ui/Button';
import { Dialog } from '@/ui/Dialog';
import { Drawer } from '@/ui/Drawer';
import { Field, Input } from '@/ui/Field';
import { Skeleton } from '@/ui/Skeleton';
import { EstadoError } from '@/components/EstadoError';
import { useRestaurarVersion, useVersiones, type Version } from './api';
import { fechaHora } from './piezas';

/**
 * Versiones — el historial de un registro, y volver a una versión anterior.
 *
 * ## Lo que cambia
 *
 * - **Restaurar ya no usa `prompt()`.** El diálogo del navegador estaba
 *   prohibido por `CLAUDE.md` desde el principio, pero la prueba que lo vigila
 *   (`test_shell.py`) solo miraba `confirm` y `alert`: por eso este sobrevivió.
 *   La prueba se amplió a `prompt` en el mismo cambio que esta pantalla.
 * - **Las fechas se ven en la hora de quien mira.** Eran
 *   `changed_at.replace('T',' ').slice(0,16)`, o sea UTC en crudo: cinco horas
 *   por delante en Bogotá.
 * - **Restaurar dice qué va a pasar** antes de hacerlo: crea una versión nueva
 *   con el contenido de la elegida, no borra lo que hay en medio.
 */
export function Versiones({ id, onCerrar }: { id: number; onCerrar: () => void }) {
  const consulta = useVersiones(id);
  const restaurar = useRestaurarVersion();
  const [restaurando, setRestaurando] = useState<number | null>(null);
  const [motivo, setMotivo] = useState('');

  const actual = consulta.data?.current_version;
  const versiones = consulta.data?.versions ?? [];

  return (
    <Drawer
      abierto
      onCambio={(v) => { if (!v) onCerrar(); }}
      titulo="Historial de versiones"
      descripcion="Cada cambio del registro, quién lo hizo y qué campos tocó."
      ancho="md"
      pie={<Button variant="ghost" onClick={onCerrar}>Cerrar</Button>}
    >
      {consulta.isPending ? (
        <div className="flex flex-col gap-3" aria-busy="true">
          {[0, 1, 2].map((i) => <Skeleton key={i} className="h-24 w-full" />)}
        </div>
      ) : consulta.isError ? (
        <EstadoError
          err={consulta.error}
          que="el historial de versiones"
          onReintentar={() => void consulta.refetch()}
        />
      ) : (
        <ol className="flex flex-col gap-2">
          {versiones.map((v) => (
            <li key={v.version}>
              <FichaDeVersion
                version={v}
                esActual={v.version === actual}
                onRestaurar={() => { setMotivo(''); setRestaurando(v.version); }}
              />
            </li>
          ))}
        </ol>
      )}

      <Dialog
        abierto={restaurando != null}
        onCambio={(v) => { if (!v) setRestaurando(null); }}
        titulo={`Restaurar la versión ${restaurando ?? ''}`}
        descripcion="Se crea una versión nueva con el contenido de esa. Lo que pasó en medio no se borra: sigue en el historial."
        ancho="max-w-md"
        pie={
          <>
            <Button variant="ghost" onClick={() => setRestaurando(null)} autoFocus>Cancelar</Button>
            <Button
              loading={restaurar.isPending}
              onClick={() => {
                if (restaurando == null) return;
                restaurar.mutate(
                  { id, version: restaurando, reason: motivo.trim() || null },
                  {
                    onSuccess: (r) => {
                      Utils.toast(r.message, 'success');
                      setRestaurando(null);
                      onCerrar();
                    },
                    onError: (err) => Utils.toast(
                      err instanceof Error ? err.message : 'No se pudo restaurar', 'error'),
                  },
                );
              }}
            >
              <i className="bi bi-arrow-counterclockwise" aria-hidden="true" /> Restaurar
            </Button>
          </>
        }
      >
        <div className="pb-2">
          <Field label="Motivo" opcional ayuda="Queda escrito en la versión que se crea.">
            <Input
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder="Por qué se vuelve a esta versión"
            />
          </Field>
        </div>
      </Dialog>
    </Drawer>
  );
}

function FichaDeVersion({
  version, esActual, onRestaurar,
}: {
  version: Version;
  esActual: boolean;
  onRestaurar: () => void;
}) {
  const cambios = version.changed_fields ?? [];
  return (
    <article className="rounded-sm border border-line p-3">
      <header className="mb-1.5 flex flex-wrap items-center gap-2">
        <strong className="text-3 font-bold text-brand-ink">v{version.version}</strong>
        <Badge tone="neutral">{version.action_label}</Badge>
        {esActual && <Badge tone="ok">actual</Badge>}
        <span className="ml-auto text-1 text-muted">
          {version.changed_by} · {fechaHora(version.changed_at)}
        </span>
      </header>

      {version.reason && (
        <p className="mb-1.5 flex items-start gap-1.5 text-2 text-muted">
          <i className="bi bi-chat-quote mt-0.5 shrink-0" aria-hidden="true" />
          <span>{version.reason}</span>
        </p>
      )}

      {cambios.length ? (
        <ul className="flex flex-col gap-0.5 text-2">
          {cambios.map((c, i) => (
            <li key={i} className="break-words">
              <strong className="text-ink">{c.label}:</strong>{' '}
              <span className="text-danger-ink line-through">{c.old || '—'}</span>
              {' → '}
              <span className="text-ok-ink">{c.new || '—'}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-2 text-muted">Sin cambios de campo</p>
      )}

      {!esActual && (
        <Button variant="secondary" size="sm" className="mt-2" onClick={onRestaurar}>
          <i className="bi bi-arrow-counterclockwise" aria-hidden="true" /> Restaurar esta versión
        </Button>
      )}
    </article>
  );
}
